import React, { useState } from 'react';
import { FileText, GitBranch, AlertTriangle, History, Users, Clock, CheckCircle, AlertCircle, Eye, Download, Plus, Search } from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';

const VersionControl = () => {
  const [activeTab, setActiveTab] = useState('document-versions');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Mock data for document versions
  const documentVersions = [
    {
      id: 1,
      name: 'Q4 Contract Template',
      version: 'v2.1',
      type: 'Contract',
      lastModified: '2024-01-15 14:30',
      modifiedBy: 'Sarah Johnson',
      size: '2.4 MB',
      status: 'Published',
      changes: 'Updated payment terms and liability clauses',
      branch: 'main'
    },
    {
      id: 2,
      name: 'Vendor Agreement',
      version: 'v1.3',
      type: 'Agreement',
      lastModified: '2024-01-14 09:15',
      modifiedBy: 'Michael Chen',
      size: '1.8 MB',
      status: 'Draft',
      changes: 'Added new compliance requirements',
      branch: 'feature/compliance'
    },
    {
      id: 3,
      name: 'Service Level Agreement',
      version: 'v3.0',
      type: 'SLA',
      lastModified: '2024-01-13 16:45',
      modifiedBy: 'Emily Rodriguez',
      size: '3.2 MB',
      status: 'Review',
      changes: 'Major revision of service metrics',
      branch: 'main'
    }
  ];

  // Mock data for change tracking
  const changeHistory = [
    {
      id: 1,
      document: 'Q4 Contract Template',
      version: 'v2.1',
      changeType: 'Modified',
      changeDescription: 'Updated payment terms from 30 to 45 days',
      changedBy: 'Sarah Johnson',
      timestamp: '2024-01-15 14:30',
      impact: 'Medium',
      approvedBy: 'Michael Chen',
      approvedAt: '2024-01-15 15:00'
    },
    {
      id: 2,
      document: 'Vendor Agreement',
      version: 'v1.3',
      changeType: 'Added',
      changeDescription: 'Added GDPR compliance clause',
      changedBy: 'Michael Chen',
      timestamp: '2024-01-14 09:15',
      impact: 'High',
      approvedBy: 'Sarah Johnson',
      approvedAt: '2024-01-14 10:30'
    },
    {
      id: 3,
      document: 'Service Level Agreement',
      version: 'v3.0',
      changeType: 'Deleted',
      changeDescription: 'Removed outdated penalty clause',
      changedBy: 'Emily Rodriguez',
      timestamp: '2024-01-13 16:45',
      impact: 'Low',
      approvedBy: 'David Kim',
      approvedAt: '2024-01-13 17:15'
    }
  ];

  // Mock data for version history
  const versionHistory = [
    {
      id: 1,
      document: 'Q4 Contract Template',
      version: 'v2.1',
      previousVersion: 'v2.0',
      createdDate: '2024-01-15',
      createdBy: 'Sarah Johnson',
      description: 'Updated payment terms and liability clauses',
      fileSize: '2.4 MB',
      downloadCount: 12,
      isCurrent: true
    },
    {
      id: 2,
      document: 'Q4 Contract Template',
      version: 'v2.0',
      previousVersion: 'v1.9',
      createdDate: '2024-01-10',
      createdBy: 'Michael Chen',
      description: 'Added new termination clause',
      fileSize: '2.3 MB',
      downloadCount: 8,
      isCurrent: false
    },
    {
      id: 3,
      document: 'Q4 Contract Template',
      version: 'v1.9',
      previousVersion: 'v1.8',
      createdDate: '2024-01-05',
      createdBy: 'Emily Rodriguez',
      description: 'Minor typo corrections',
      fileSize: '2.2 MB',
      downloadCount: 5,
      isCurrent: false
    }
  ];

  // Mock data for branch management
  const branches = [
    {
      id: 1,
      name: 'main',
      description: 'Production branch for stable releases',
      lastCommit: '2024-01-15 14:30',
      commitsAhead: 0,
      commitsBehind: 0,
      status: 'Active',
      protected: true
    },
    {
      id: 2,
      name: 'feature/compliance',
      description: 'New compliance requirements implementation',
      lastCommit: '2024-01-14 09:15',
      commitsAhead: 3,
      commitsBehind: 1,
      status: 'Active',
      protected: false
    },
    {
      id: 3,
      name: 'hotfix/payment-terms',
      description: 'Urgent fix for payment terms',
      lastCommit: '2024-01-12 11:20',
      commitsAhead: 1,
      commitsBehind: 0,
      status: 'Merged',
      protected: false
    }
  ];

  const tabs = [
    { id: 'document-versions', name: 'Document Versions', icon: <FileText size={18} /> },
    { id: 'change-tracking', name: 'Change Tracking', icon: <History size={18} /> },
    { id: 'version-history', name: 'Version History', icon: <Clock size={18} /> },
    { id: 'branch-management', name: 'Branch Management', icon: <GitBranch size={18} /> }
  ];

  const handleAction = (action, data) => {
    setModalData({ action, data });
    setShowModal(true);
  };

  const handleConfirm = () => {
    // Handle action confirmation
    setShowModal(false);
    setModalData(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': return 'bg-green-100 text-green-800';
      case 'Draft': return 'bg-yellow-100 text-yellow-800';
      case 'Review': return 'bg-blue-100 text-blue-800';
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Merged': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'Added': return <Plus size={16} className="text-green-600" />;
      case 'Modified': return <AlertCircle size={16} className="text-yellow-600" />;
      case 'Deleted': return <AlertCircle size={16} className="text-red-600" />;
      default: return <AlertCircle size={16} className="text-gray-600" />;
    }
  };

  const renderDocumentVersions = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {documentVersions.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Documents
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileText size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {documentVersions.filter(d => d.status === 'Published').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Published Versions
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#ffc107', fontWeight: '700', marginBottom: '4px' }}>
                    {documentVersions.filter(d => d.status === 'Draft').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Draft Versions
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#ffc10720',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AlertCircle size={24} style={{ color: '#ffc107' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#17a2b8', fontWeight: '700', marginBottom: '4px' }}>
                    {branches.filter(b => b.status === 'Active').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Active Branches
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#17a2b820',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GitBranch size={24} style={{ color: '#17a2b8' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={documentVersions}
          columns={[
            {
              key: 'name',
              label: 'Document Name',
              width: '250px',
              render: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={14} style={{ color: 'var(--primary-color)' }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{item.name}</div>
                    <small style={{ color: '#6b7280', fontSize: '11px' }}>{item.type} • {item.size}</small>
                  </div>
                </div>
              )
            },
            {
              key: 'version',
              label: 'Version',
              width: '100px',
              type: 'badge',
              badgeClass: () => 'badge-primary'
            },
            {
              key: 'branch',
              label: 'Branch',
              width: '120px',
              type: 'badge',
              badgeClass: () => 'badge-secondary'
            },
            {
              key: 'status',
              label: 'Status',
              width: '100px',
              type: 'badge',
              badgeClass: (value) => {
                const statusClasses = {
                  'Published': 'badge-success',
                  'Draft': 'badge-warning',
                  'Review': 'badge-info'
                };
                return statusClasses[value] || 'badge-secondary';
              }
            },
            {
              key: 'lastModified',
              label: 'Last Modified',
              width: '140px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'modifiedBy',
              label: 'Modified By',
              width: '150px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            }
          ]}
          title="Document Versions"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view', item)}
          onEdit={(item) => handleAction('edit', item)}
          onDelete={(item) => handleAction('delete', item)}
          searchPlaceholder="Search documents by name, type, or version..."
          loading={false}
          emptyMessage="No document versions found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderChangeTracking = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {changeHistory.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Changes
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <History size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#dc3545', fontWeight: '700', marginBottom: '4px' }}>
                    {changeHistory.filter(c => c.impact === 'High').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    High Impact
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#dc354520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AlertTriangle size={24} style={{ color: '#dc3545' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#ffc107', fontWeight: '700', marginBottom: '4px' }}>
                    {changeHistory.filter(c => c.impact === 'Medium').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Medium Impact
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#ffc10720',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AlertTriangle size={24} style={{ color: '#ffc107' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {changeHistory.filter(c => c.impact === 'Low').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Low Impact
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={changeHistory}
          columns={[
            {
              key: 'changeType',
              label: 'Change Type',
              width: '120px',
              render: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {getChangeTypeIcon(item.changeType)}
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{item.changeType}</span>
                </div>
              )
            },
            {
              key: 'document',
              label: 'Document',
              width: '180px',
              render: (value, row) => (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{value}</div>
                  <small style={{ color: '#6b7280', fontSize: '11px' }}>{row.version}</small>
                </div>
              )
            },
            {
              key: 'changeDescription',
              label: 'Change Description',
              width: '200px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'impact',
              label: 'Impact',
              width: '100px',
              type: 'badge',
              badgeClass: (value) => {
                const impactClasses = {
                  'High': 'badge-danger',
                  'Medium': 'badge-warning',
                  'Low': 'badge-success'
                };
                return impactClasses[value] || 'badge-secondary';
              }
            },
            {
              key: 'changedBy',
              label: 'Changed By',
              width: '150px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'timestamp',
              label: 'Timestamp',
              width: '160px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            }
          ]}
          title="Change Tracking"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view-details', item)}
          onEdit={(item) => handleAction('revert', item)}
          onDelete={(item) => handleAction('revert', item)}
          searchPlaceholder="Search changes by document, type, or user..."
          loading={false}
          emptyMessage="No change history found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderVersionHistory = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {versionHistory.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Versions
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {versionHistory.filter(v => v.isCurrent).length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Current Versions
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#17a2b8', fontWeight: '700', marginBottom: '4px' }}>
                    {versionHistory.reduce((sum, v) => sum + v.downloadCount, 0)}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Downloads
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#17a2b820',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Download size={24} style={{ color: '#17a2b8' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#6f42c1', fontWeight: '700', marginBottom: '4px' }}>
                    {new Set(versionHistory.map(v => v.createdBy)).size}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Active Contributors
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#6f42c120',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={24} style={{ color: '#6f42c1' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={versionHistory}
          columns={[
            {
              key: 'document',
              label: 'Document',
              width: '200px',
              render: (value) => (
                <span style={{ fontSize: '14px', fontWeight: '500' }}>{value}</span>
              )
            },
            {
              key: 'version',
              label: 'Version',
              width: '120px',
              render: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="badge badge-primary" style={{ fontSize: '11px' }}>
                    {item.version}
                  </span>
                  {item.isCurrent && (
                    <span className="badge badge-success" style={{ fontSize: '11px' }}>
                      Current
                    </span>
                  )}
                </div>
              )
            },
            {
              key: 'previousVersion',
              label: 'Previous Version',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'createdDate',
              label: 'Created Date',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'createdBy',
              label: 'Created By',
              width: '150px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'downloadCount',
              label: 'Downloads',
              width: '100px',
              render: (value) => (
                <span style={{ fontSize: '13px', fontWeight: '500' }}>{value}</span>
              )
            }
          ]}
          title="Version History"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view-version', item)}
          onEdit={(item) => handleAction('download-version', item)}
          onDelete={(item) => handleAction('download-version', item)}
          searchPlaceholder="Search versions by document, creator, or version..."
          loading={false}
          emptyMessage="No version history found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderBranchManagement = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {branches.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Branches
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GitBranch size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {branches.filter(b => b.status === 'Active').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Active Branches
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#dc3545', fontWeight: '700', marginBottom: '4px' }}>
                    {branches.filter(b => b.protected).length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Protected Branches
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#dc354520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AlertTriangle size={24} style={{ color: '#dc3545' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#17a2b8', fontWeight: '700', marginBottom: '4px' }}>
                    {branches.filter(b => b.status === 'Merged').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Merged Branches
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#17a2b820',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={24} style={{ color: '#17a2b8' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={branches}
          columns={[
            {
              key: 'name',
              label: 'Branch Name',
              width: '180px',
              render: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <GitBranch size={14} style={{ color: 'var(--primary-color)' }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{item.name}</div>
                    {item.protected && (
                      <span className="badge badge-danger" style={{ fontSize: '10px' }}>
                        Protected
                      </span>
                    )}
                  </div>
                </div>
              )
            },
            {
              key: 'description',
              label: 'Description',
              width: '200px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'status',
              label: 'Status',
              width: '100px',
              type: 'badge',
              badgeClass: (value) => {
                const statusClasses = {
                  'Active': 'badge-success',
                  'Merged': 'badge-info',
                  'Stale': 'badge-warning'
                };
                return statusClasses[value] || 'badge-secondary';
              }
            },
            {
              key: 'lastCommit',
              label: 'Last Commit',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'commitsAhead',
              label: 'Commits Ahead',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#28a745' }}>+{value}</span>
              )
            },
            {
              key: 'commitsBehind',
              label: 'Commits Behind',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#dc3545' }}>-{value}</span>
              )
            }
          ]}
          title="Branch Management"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view-branch', item)}
          onEdit={(item) => handleAction('merge-branch', item)}
          onDelete={(item) => handleAction('merge-branch', item)}
          searchPlaceholder="Search branches by name, status, or description..."
          loading={false}
          emptyMessage="No branches found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'document-versions':
        return renderDocumentVersions();
      case 'change-tracking':
        return renderChangeTracking();
      case 'version-history':
        return renderVersionHistory();
      case 'branch-management':
        return renderBranchManagement();
      default:
        return renderDocumentVersions();
    }
  };

  return (
    <div style={{
      padding: '24px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#1f2937',
          margin: '0 0 8px 0',
          background: 'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Version Control
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: 0
        }}>
          Document versioning, change tracking, and version history management
        </p>
      </div>

      {/* Tabbed Navigation */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #e5e7eb',
          gap: '0'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color: activeTab === tab.id ? '#007B8F' : '#6b7280',
                borderBottom: activeTab === tab.id ? '3px solid #007B8F' : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px'
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <div style={{
                color: activeTab === tab.id ? '#007B8F' : '#9ca3af'
              }}>
                {tab.icon}
              </div>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        title={modalData?.action ? `Confirm ${modalData.action}` : 'Confirm Action'}
        message={modalData?.data ? `Are you sure you want to ${modalData.action} ${modalData.data.name || modalData.data.document || 'this item'}?` : 'Are you sure you want to perform this action?'}
      />
    </div>
  );
};

export default VersionControl;
